import { useContext, useState } from "react";
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  Snackbar,
  Grid2,
} from "@mui/material";
import { Mail, Send, ArrowRight } from "lucide-react";
import { useDispatch } from "react-redux";
import CustomLoader from "../../../components/CustomLoader";
import { set } from "date-fns";
import { createLeads } from "../../../reducers/analytics.slice";
import { AuthContext } from "../../../context/authContext";

export default function Contact() {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const dispatch = useDispatch();
  const [showLoader, setShowLoader] = useState(false);
  const { showToast } = useContext(AuthContext);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowLoader(true);
    dispatch(createLeads(formData)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        setShowLoader(false);
        return;
      }
      showToast("Message sent successfully!", "success");
      setShowLoader(false);
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        subject: "",
        message: "",
      });
    });
  };

  return (
    <Box
      id="contact"
      sx={{
        py: { xs: 8, md: 12 },
        background: "linear-gradient(180deg, #101010 0%, #1e1e1e 100%)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "1px",
          background:
            "linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)",
        },
      }}
    >
      <CustomLoader open={showLoader} />
      <Container maxWidth="lg">
        <Grid2 container spacing={8} alignItems="center">
          <Grid2 item size={{ xs: 12, md: 5 }}>
            <Box sx={{ position: "relative" }}>
              <Typography
                variant="h2"
                sx={{
                  mb: 3,
                  fontSize: { xs: "2rem", sm: "2.25rem", md: "2.5rem" },
                  fontWeight: 800,
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: -12,
                    left: 0,
                    width: 80,
                    height: 4,
                    background: "linear-gradient(90deg, #00ADB5, transparent)",
                    borderRadius: "2px",
                  },
                }}
              >
                Get in Touch
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: "text.secondary",
                  mb: 4,
                  fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                  lineHeight: 1.6,
                  maxWidth: "500px",
                }}
              >
                Have questions about RSC? We're here to help and would love to
                hear from you.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 3,
                  bgcolor: "rgba(0,173,181,0.1)",
                  borderRadius: "16px",
                  border: "1px solid rgba(0,173,181,0.2)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
                    bgcolor: "rgba(0,173,181,0.15)",
                  },
                }}
              >
                <Box
                  sx={{
                    color: "#00ADB5",
                    p: 2,
                    borderRadius: "12px",
                    bgcolor: "rgba(0,173,181,0.2)",
                  }}
                >
                  <Mail size={24} />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: "#00ADB5", fontWeight: 600, mb: 0.5 }}
                  >
                    Email Us
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    support@rs-chain.com
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid2>

          <Grid2 item size={{ xs: 12, md: 7 }}>
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                p: { xs: 3, sm: 4 },
                bgcolor: "rgba(255,255,255,0.02)",
                borderRadius: "24px",
                border: "1px solid rgba(255,255,255,0.1)",
                backdropFilter: "blur(10px)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "1px",
                  background:
                    "linear-gradient(90deg, transparent, rgba(0,173,181,0.2), transparent)",
                },
              }}
            >
              <Grid2 container spacing={3}>
                <Grid2 item size={{ xs: 12 }} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    variant="filled"
                    sx={{
                      "& .MuiFilledInput-root": {
                        bgcolor: "rgba(0,0,0,0.2)",
                        "&:hover": {
                          bgcolor: "rgba(0,0,0,0.3)",
                        },
                        "&.Mui-focused": {
                          bgcolor: "rgba(0,0,0,0.3)",
                        },
                      },
                    }}
                  />
                </Grid2>
                <Grid2 item size={{ xs: 12 }} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    variant="filled"
                    sx={{
                      "& .MuiFilledInput-root": {
                        bgcolor: "rgba(0,0,0,0.2)",
                        "&:hover": {
                          bgcolor: "rgba(0,0,0,0.3)",
                        },
                        "&.Mui-focused": {
                          bgcolor: "rgba(0,0,0,0.3)",
                        },
                      },
                    }}
                  />
                </Grid2>
                <Grid2 item size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    variant="filled"
                    sx={{
                      "& .MuiFilledInput-root": {
                        bgcolor: "rgba(0,0,0,0.2)",
                        "&:hover": {
                          bgcolor: "rgba(0,0,0,0.3)",
                        },
                        "&.Mui-focused": {
                          bgcolor: "rgba(0,0,0,0.3)",
                        },
                      },
                    }}
                  />
                </Grid2>
                <Grid2 item size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    variant="filled"
                    sx={{
                      "& .MuiFilledInput-root": {
                        bgcolor: "rgba(0,0,0,0.2)",
                        "&:hover": {
                          bgcolor: "rgba(0,0,0,0.3)",
                        },
                        "&.Mui-focused": {
                          bgcolor: "rgba(0,0,0,0.3)",
                        },
                      },
                    }}
                  />
                </Grid2>
                <Grid2 item size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    required
                    variant="filled"
                    sx={{
                      "& .MuiFilledInput-root": {
                        bgcolor: "rgba(0,0,0,0.2)",
                        "&:hover": {
                          bgcolor: "rgba(0,0,0,0.3)",
                        },
                        "&.Mui-focused": {
                          bgcolor: "rgba(0,0,0,0.3)",
                        },
                      },
                    }}
                  />
                </Grid2>
              </Grid2>
              <Button
                type="submit"
                variant="contained"
                size="large"
                endIcon={<Send size={18} />}
                sx={{
                  mt: 4,
                  height: 56,
                  borderRadius: "28px",
                  px: 4,
                  bgcolor: "#00ADB5",
                  "&:hover": {
                    bgcolor: "#00959c",
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 20px rgba(0,173,181,0.3)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Send Message
              </Button>
            </Box>
          </Grid2>
        </Grid2>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
